/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */
import type { FloraOutput } from '$lib/apis/webchat/type';
import { get, writable } from 'svelte/store';
import { chatSession, notificationSoundEnabled, sessionTimeout } from './chatSessionStore';
import { soundNotification } from '$lib/utilities/soundNotification';
import { datadogLogs } from '@datadog/browser-logs';
import { stopTimer } from '$lib/utilities/disconnectConversation';

const ding = '/chat/ding.mp3';
const userManuallyClosedSocket = writable(false)
const MAXIMUM_TRY_COUNT = 7;

export const socketState = writable<{
    socket: WebSocket | null;
    status: string;
    messages: {
        sender: string;
        message: string;
        type: string;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        params?: Record<string, any>;
    }[];
}>({
    socket: null,
    status: 'disconnected',
    messages: [] as {
        sender: string;
        message: string;
        type: string;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        params?: Record<string, any>;
    }[]
});

let socket: WebSocket | null = null;

function reconnectConversation(url: string, conversation_id: string, brand: string) {
    const retryAttempts = get(chatSession)?.reconnectAttempts
    if (retryAttempts >= MAXIMUM_TRY_COUNT) {
        socketState.update(state => ({ ...state, status: 'disconnected', socket: null }))
        if (!get(chatSession)?.reconnectFailureMessageSent) {
            chatSession.update((old) => {
                const updatedSession = {
                    ...old,
                    messages: [
                        ...old.messages,
                        {
                            sender: 'bot',
                            message: 'It looks like there was an error with the connection to your live representative. Please close this conversation and try again. Apologies for the inconvenience.',
                            type: 'text',
                            params: {} as FloraOutput['params']
                        }
                    ],
                };
                sessionStorage.setItem('conversation', JSON.stringify(updatedSession));
                return updatedSession;
            });
            chatSession.update(session => ({ ...session, reconnectFailureMessageSent: true }))
        }
        datadogLogs.logger.info(`[Conv-ui] Dynamics Retry Limit Reached- conversation_id: ${conversation_id} brand:${brand}`)
        return
    }
    chatSession.update((session) => ({ ...session, reconnectAttempts: (retryAttempts + 1) }))
    socketState.update(state => ({ ...state, status: 'reconnecting' }))
    setTimeout(() => {
        socket = null
        connect(url, conversation_id, brand)
    }, 3000)
    datadogLogs.logger.info(`[Conv-ui] Dynamics Retry Attempt - conversation_id: ${conversation_id} brand:${brand}`)
}

export function connect(url: string, conversation_id: string, brand: string) {
    if (socket) {
        return;
    }
    userManuallyClosedSocket.set(false)
    socket = new WebSocket(url);
    socketState.update(state => ({ ...state, status: 'connecting' }));

    socket.onopen = () => {
        socketState.update(state => ({ ...state, status: 'connected', socket }));
    };

    socket.onmessage = (event) => {
        const message = JSON.parse(event.data);
        let surveyLink: string = ''
        socketState.update(state => ({
            ...state,
            messages: [...state.messages, message]
        }));
        if (message.o && message?.conversational_ui_params) {
            if (message?.conversational_ui_params?.session_timeout) {
                sessionTimeout.set(true)
                stopTimer(conversation_id)
                datadogLogs.logger.info(`[Conv-ui] Session Timeout sent - conversation_id: ${conversation_id} brand:${brand}`)
            }
            chatSession.update((old) => {
                const updatedSession = {
                    ...old,
                    messages: [
                        ...old.messages,
                        {
                            sender: 'agent',
                            message: message.o,
                            type: 'text',
                            params: {} as FloraOutput['params']
                        }
                    ],
                };
                sessionStorage.setItem('conversation', JSON.stringify(updatedSession));
                return updatedSession;
            });
            return
        }
        if (typeof message.o === 'string' && message.o.includes('{')) {
            const parseSurvey = JSON.parse(message.o)
            surveyLink = parseSurvey?.richLinkData.url
        }
        if (message.o) {
            chatSession.update((old) => {
                const updatedSession = {
                    ...old,
                    messages: [
                        ...old.messages,
                        {
                            sender: 'agent',
                            message: surveyLink ? 'Have a nice day' : message.o,
                            type: 'text',
                            params: { survey_link: surveyLink ? surveyLink : '' } as FloraOutput['params']
                        }
                    ],
                };
                sessionStorage.setItem('conversation', JSON.stringify(updatedSession));
                return updatedSession;
            });
            datadogLogs.logger.info(`[Conv-ui] Dynamics Agent Message - conversation_id: ${conversation_id} brand:${brand} message: ${message.o}`)
            if (get(notificationSoundEnabled)) {
                const { play } = soundNotification(ding);
                play()
            }
        }
    };

    socket.onerror = (error) => {
        if (!get(userManuallyClosedSocket)) {
            reconnectConversation(url, conversation_id, brand);
            return;
        }
        console.error('WebSocket Error:', error);
        socketState.update(state => ({ ...state, status: 'error' }));
    };

    socket.onclose = (event) => {
        if (!get(userManuallyClosedSocket) && event.code === 1006) {
            reconnectConversation(url, conversation_id, brand);
            return;
        }
        socketState.update(state => ({ ...state, status: 'disconnected', socket: null }));
        socket = null;
    };
    return socket
}

export function socketDisconnect() {
    if (socket) {
        userManuallyClosedSocket.set(true);
        socket.close();
        socket = null;
        socketState.update(state => ({ ...state, status: 'disconnected', socket: null }));
    }
}

export function sendDynamicsMessage(message: string, conversation_id: string, brand: string) {
    if (socket && socket.readyState === WebSocket.OPEN) {
        socket.send(JSON.stringify({ o: message, source: 'customer' }));
        datadogLogs.logger.info(`[Conv-ui] Dynamics Customer Message - conversation_id: ${conversation_id} brand:${brand} message: ${message}`)
    } else {
        console.warn('WebSocket is not connected.');
    }
}